import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { APP_FONT } from '@/constants';
import type { SpellcheckIssue } from '@/api/spellcheck';

type AnchorPoint = {
    clientX: number;
    clientY: number;
    pageX: number;
    pageY: number;
};

type SpellcheckSuggestionPopoverProps = {
    issue: SpellcheckIssue;
    anchorPoint: AnchorPoint;
    onApply: (suggestion: string) => void;
    onClose: () => void;
};

const POPOVER_WIDTH = 224;
const VIEWPORT_MARGIN = 8;
const SPELLCHECK_POPOVER_OPEN_EVENT = 'laudopsi:spellcheck-popover-open';

export default function SpellcheckSuggestionPopover({
    issue,
    anchorPoint,
    onApply,
    onClose,
}: SpellcheckSuggestionPopoverProps) {
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const popoverIdRef = useRef(
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : String(Math.random()),
    );
    const onCloseRef = useRef(onClose);
    const [position, setPosition] = useState<{
        left: number;
        top: number;
        visibility: CSSProperties['visibility'];
    }>({
        left: anchorPoint.clientX + VIEWPORT_MARGIN,
        top: anchorPoint.clientY + VIEWPORT_MARGIN,
        visibility: 'hidden',
    });

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        const popoverId = popoverIdRef.current;
        const handlePopoverOpened = (event: Event) => {
            if (!(event instanceof CustomEvent)) return;
            if (event.detail?.id === popoverId) return;

            onCloseRef.current();
        };

        document.addEventListener(SPELLCHECK_POPOVER_OPEN_EVENT, handlePopoverOpened);
        document.dispatchEvent(
            new CustomEvent(SPELLCHECK_POPOVER_OPEN_EVENT, {
                detail: { id: popoverId },
            }),
        );

        return () => {
            document.removeEventListener(SPELLCHECK_POPOVER_OPEN_EVENT, handlePopoverOpened);
        };
    }, []);

    useLayoutEffect(() => {
        const popover = popoverRef.current;

        if (!popover) return;

        const width = popover.offsetWidth || POPOVER_WIDTH;
        const height = popover.offsetHeight;
        let left = anchorPoint.clientX + VIEWPORT_MARGIN;
        let top = anchorPoint.clientY + VIEWPORT_MARGIN;

        if (left + width + VIEWPORT_MARGIN > window.innerWidth) {
            left = anchorPoint.clientX - width - VIEWPORT_MARGIN;
        }

        if (top + height + VIEWPORT_MARGIN > window.innerHeight) {
            top = anchorPoint.clientY - height - VIEWPORT_MARGIN;
        }

        setPosition({
            left: Math.max(
                VIEWPORT_MARGIN,
                Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN),
            ),
            top: Math.max(
                VIEWPORT_MARGIN,
                Math.min(top, window.innerHeight - height - VIEWPORT_MARGIN),
            ),
            visibility: 'visible',
        });
    }, [anchorPoint, issue]);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            const popover = popoverRef.current;

            if (!popover || !(event.target instanceof Node)) return;
            if (popover.contains(event.target)) return;

            onCloseRef.current();
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onCloseRef.current();
            }
        };

        const handleScroll = () => {
            onCloseRef.current();
        };

        const previewContainer = document.querySelector('[data-preview-container]');

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        previewContainer?.addEventListener('scroll', handleScroll, {
            passive: true,
        });
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
            previewContainer?.removeEventListener('scroll', handleScroll);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <div
            ref={popoverRef}
            className="fixed z-[90] rounded-lg border border-border bg-card p-2 text-left shadow-xl"
            style={{
                left: position.left,
                top: position.top,
                width: POPOVER_WIDTH,
                visibility: position.visibility,
                fontFamily: APP_FONT,
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            <div className="px-2 pb-1 text-[11px] font-medium tracking-wide text-muted-foreground">
                Correção sugerida
            </div>
            {issue.suggestions.length > 0 ? (
                <div className="flex flex-col gap-1">
                    {issue.suggestions.slice(0, 3).map((suggestion) => (
                        <button
                            key={suggestion}
                            type="button"
                            className="rounded-md px-2 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                            onClick={() => onApply(suggestion)}
                        >
                            {suggestion}
                        </button>
                    ))}
                </div>
            ) : (
                <div className="px-2 py-1 text-xs text-muted-foreground">
                    Sem sugestão automática.
                </div>
            )}
        </div>,
        document.body,
    );
}
